import React, { useState, useEffect, useRef, useCallback } from "react";
import ChatbotModal from "../chatbotModal";
import { tokenService } from "../../utils/tokenService";

// ── Constants ─────────────────────────────────────────────────────────────────
const BTN_SIZE       = 48;          // button diameter in px
const EDGE_MARGIN    = 16;          // min gap from viewport edges
const SNAP_THRESHOLD = 8;           // px from edge to trigger snap
const STORAGE_KEY    = "dmrc_chatbot_btn_pos";

interface Pos { x: number; y: number }

// Clamp position so button never leaves the viewport
const clamp = (pos: Pos): Pos => ({
    x: Math.min(Math.max(pos.x, EDGE_MARGIN), window.innerWidth  - BTN_SIZE - EDGE_MARGIN),
    y: Math.min(Math.max(pos.y, EDGE_MARGIN), window.innerHeight - BTN_SIZE - EDGE_MARGIN),
});

// Snap to the nearest horizontal edge if within threshold
const snapToEdge = (pos: Pos): Pos => {
    const snapLeft  = pos.x <= SNAP_THRESHOLD + EDGE_MARGIN;
    const snapRight = pos.x >= window.innerWidth - BTN_SIZE - EDGE_MARGIN - SNAP_THRESHOLD;
    return {
        x: snapLeft  ? EDGE_MARGIN
         : snapRight ? window.innerWidth - BTN_SIZE - EDGE_MARGIN
         : pos.x,
        y: pos.y,
    };
};

const defaultPos = (): Pos => ({
    x: window.innerWidth  - BTN_SIZE - EDGE_MARGIN - 10,
    y: window.innerHeight - BTN_SIZE - EDGE_MARGIN - 80,
});

const loadPos = (): Pos => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const p = JSON.parse(raw) as Pos;
            return clamp(p);         // re-clamp in case viewport resized
        }
    } catch { /* ignore */ }
    return defaultPos();
};

// ─────────────────────────────────────────────────────────────────────────────
const ChatbotFloatingButton: React.FC = () => {
    const [isEmployee,  setIsEmployee]  = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pos,         setPos]         = useState<Pos>(loadPos);
    const [dragging,    setDragging]    = useState(false);
    const [hasMoved,    setHasMoved]    = useState(false); // distinguish click vs drag

    const btnRef   = useRef<HTMLButtonElement>(null);
    const dragData = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });
    const rafId    = useRef<number>(0);

    // ── Auth check ────────────────────────────────────────────────────────────
    useEffect(() => {
        const token = tokenService.getToken();
        const raw   = localStorage.getItem("user");
        if (token && raw && raw !== "undefined") {
            try {
                const u = JSON.parse(raw);
                setIsEmployee((u?.user_type ?? u?.role) === "EMPLOYEE");
            } catch { setIsEmployee(false); }
        }
    }, []);

    // ── Persist position ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!dragging) {
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
        }
    }, [pos, dragging]);

    // ── Re-clamp on window resize ─────────────────────────────────────────────
    useEffect(() => {
        const onResize = () => setPos((p) => clamp(p));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ── Drag start (mouse + touch) ────────────────────────────────────────────
    const startDrag = useCallback((clientX: number, clientY: number) => {
        setHasMoved(false);
        dragData.current = {
            startX:  clientX,
            startY:  clientY,
            originX: pos.x,
            originY: pos.y,
        };
        setDragging(true);
    }, [pos]);

    const onMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        startDrag(e.clientX, e.clientY);
    };

    const onTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        startDrag(t.clientX, t.clientY);
    };

    // ── Drag move ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!dragging) return;

        const move = (clientX: number, clientY: number) => {
            const dx = clientX - dragData.current.startX;
            const dy = clientY - dragData.current.startY;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setHasMoved(true);

            cancelAnimationFrame(rafId.current);
            rafId.current = requestAnimationFrame(() => {
                setPos(clamp({
                    x: dragData.current.originX + dx,
                    y: dragData.current.originY + dy,
                }));
            });
        };

        const end = (clientX: number, clientY: number) => {
            setDragging(false);
            const dx = clientX - dragData.current.startX;
            const dy = clientY - dragData.current.startY;
            setPos(snapToEdge(clamp({
                x: dragData.current.originX + dx,
                y: dragData.current.originY + dy,
            })));
        };

        const onMouseMove = (e: MouseEvent) => move(e.clientX, e.clientY);
        const onMouseUp   = (e: MouseEvent) => end(e.clientX, e.clientY);
        const onTouchMove = (e: TouchEvent) => { const t = e.touches[0]; move(t.clientX, t.clientY); };
        const onTouchEnd  = (e: TouchEvent) => { const t = e.changedTouches[0]; end(t.clientX, t.clientY); };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup",   onMouseUp);
        document.addEventListener("touchmove", onTouchMove, { passive: true });
        document.addEventListener("touchend",  onTouchEnd);

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup",   onMouseUp);
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend",  onTouchEnd);
            cancelAnimationFrame(rafId.current);
        };
    }, [dragging]);

    // ── Click: only open if user didn't drag ──────────────────────────────────
    const handleClick = () => {
        if (!hasMoved) setIsModalOpen(true);
    };

    if (!isEmployee) return null;

    return (
        <>
            {/* Draggable floating button */}
            <button
                ref={btnRef}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
                onClick={handleClick}
                title="Chat with DMRC Assistant — drag to reposition"
                aria-label="Open Chatbot"
                style={{
                    position:        "fixed",
                    left:            pos.x,
                    top:             pos.y,
                    width:           BTN_SIZE,
                    height:          BTN_SIZE,
                    borderRadius:    "50%",
                    backgroundColor: "white",
                    border:          "none",
                    cursor:          dragging ? "grabbing" : "grab",
                    boxShadow:       dragging
                        ? "0 12px 32px rgba(0,0,0,0.28), 0 0 0 3px rgba(192,40,44,0.25)"
                        : "0 4px 16px rgba(0,0,0,0.20)",
                    display:         "flex",
                    alignItems:      "center",
                    justifyContent:  "center",
                    zIndex:          9997,
                    padding:         0,
                    // Snap spring animation only when NOT dragging
                    transition:      dragging
                        ? "box-shadow 0.15s"
                        : "left 0.25s cubic-bezier(0.34,1.56,0.64,1), top 0.15s ease, box-shadow 0.2s, transform 0.2s",
                    transform:       dragging ? "scale(1.12)" : "scale(1)",
                    userSelect:      "none",
                    WebkitUserSelect:"none",
                    touchAction:     "none",
                }}
            >
                <img
                    src="/assets/img/login/ladies.png"
                    alt="DMRC Assistant"
                    draggable={false}
                    style={{
                        width: "100%", height: "100%",
                        borderRadius: "50%", objectFit: "contain",
                        transform: "scale(1.15)", pointerEvents: "none",
                    }}
                />

                {/* Subtle "drag" badge — only visible on hover */}
                <span style={{
                    position:        "absolute",
                    bottom:          -6,
                    left:            "50%",
                    transform:       "translateX(-50%)",
                    backgroundColor: "#c0282c",
                    color:           "white",
                    fontSize:        9,
                    fontWeight:      700,
                    letterSpacing:   "0.04em",
                    padding:         "1px 6px",
                    borderRadius:    6,
                    whiteSpace:      "nowrap",
                    pointerEvents:   "none",
                    opacity:         dragging ? 1 : 0,
                    transition:      "opacity 0.2s",
                }}>
                    DRAG
                </span>
            </button>

            {/* Tooltip on hover (non-dragging) */}
            {!dragging && (
                <style>{`
                    button[aria-label="Open Chatbot"]:hover {
                        transform: scale(1.08) !important;
                        box-shadow: 0 8px 24px rgba(0,0,0,0.28) !important;
                    }
                `}</style>
            )}

            <ChatbotModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};

export default ChatbotFloatingButton;
